import {Button} from "../../../../components/common/Button";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Controller, SubmitHandler, useForm} from "react-hook-form";
import {savedPayoutV2Data} from "../../redux/formDataSlice";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {ContentWrapper, Error, InputWrapper, ItemWrapper} from "../styles";
import CustomSelect from "../../../../components/common/CustomSelect";
import {GHS_PAYMENT_DESCRIPTION} from "../../const";
import {Input} from "../../../../components/common/Input";
import {fetchBankList} from "../../redux/slice";
import {capitalizeCurrencyCode, sanitizeAccountNumberInput, GHS_ACCOUNT_NUMBER_MAX_LENGTH, getAccountNumberPattern, getAccountNumberErrorMessage, validateGhsPhoneNumber} from "../../../../utils/helpers";
import {useParams} from "react-router-dom";
import {InputPhone} from "../../../../components/common/InputPhone";
import {usePopulateFormFromSavedData} from "../hooks";
import { accountNameValidator, validateAccountName } from "../../../../utils/validateAccountName";

type GHSBeneficiaryTypes = {
    handleNext?: () => void;
    handlePrevious?: () => void;
    paymentMethod?: string;
    paymentMethodName?: string;
    bankName?: string;
    bankCode?: string;
    bankAddress?: string;
    accountNumber?: string;
    accountName?: string;
    paymentDescription?: string;
    banks?: string[];
    phoneNumber?: string;
    bankId?: number;
};

const Beneficiary = ({handleNext, handlePrevious}: GHSBeneficiaryTypes) => {
    const dispatch = useAppDispatch();
    const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);
    const {businessId} = useAppSelector((state) => state.users);
    const {keys} = useAppSelector((state) => state.settings);
    const [userData, setUserData] = useState<GHSBeneficiaryTypes>();
    const {currencyCode} = useParams();

    const PAYMENT_METHOD = getSavedData?.paymentMethod?.includes("BANK_ACCOUNT");
    const accountNumberPattern = getAccountNumberPattern(currencyCode);
    const accountNumberErrorMessage = getAccountNumberErrorMessage(currencyCode);

    const {
        handleSubmit,
        control,
        formState: {errors, isValid},
        setValue,
        watch,
    } = useForm<GHSBeneficiaryTypes>({mode: "onChange"});

    const watchAccountNumber = watch("accountNumber");

    const publicKey = keys?.data?.publicKey;

    usePopulateFormFromSavedData(getSavedData, setValue);

    const handleDataUpdate = (field: keyof GHSBeneficiaryTypes, value: any) => {
        setUserData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    useEffect(() => {
        const fetchBanks = async () => {
            const action = await dispatch(
                fetchBankList({
                    xAuthToken: publicKey,
                    country: capitalizeCurrencyCode(currencyCode),
                    type: !PAYMENT_METHOD ? "mobile-money" : undefined,
                }),
            );

            if (fetchBankList.fulfilled.match(action)) {
                const uniqueBanks = Array.from(
                    new Map(
                        action.payload.data.map((bank: any) => [bank.code, bank]),
                    ).values(),
                );

                handleDataUpdate("banks", uniqueBanks);
            }
        };

        fetchBanks().then((r) => r);
    }, [businessId, publicKey, currencyCode, PAYMENT_METHOD]);

    const bankOptions = useMemo(
        () =>
            userData?.banks?.map((bank: any) => ({
                value: bank.code,
                label: bank.name,
                id: Math.trunc(bank.id)
            })),
        [userData?.banks],
    );

    const onSubmit: SubmitHandler<GHSBeneficiaryTypes> = useCallback(
        (data) => {
            const selectedBank = bankOptions?.find(
                (bank) => bank.value === data.bankCode,
            );

            const combinedData = {
                ...userData,
                ...data,
                bankName: selectedBank?.label,
                bankCode: selectedBank?.value,
            };

            dispatch(savedPayoutV2Data(combinedData));

            handleNext?.();
        },
        [dispatch, userData, bankOptions, handleNext],
    );

    return (
        <ContentWrapper>
            <div>
                <Controller
                    name="paymentMethodName"
                    control={control}
                    rules={{required: false}}
                    render={({field}) => {
                        return (
                            <CustomSelect
                                {...field}
                                $width="100%"
                                defaultValue={getSavedData?.paymentMethodName}
                                placeholder="Select payment method"
                                disabled
                                lightBorder
                            />
                        );
                    }}
                />
            </div>


            {PAYMENT_METHOD ? (
                <>
                    <div>
                        <Controller
                            name="bankCode"
                            control={control}
                            rules={{required: "Bank name is required"}}
                            render={({field}) => {
                                return (
                                    <CustomSelect
                                        {...field}
                                        placeholder={"Bank name"}
                                        $width={"100%"}
                                        options={bankOptions}
                                        showSearch
                                        lightBorder
                                        filterOption={(inputValue, option) =>
                                            option.label.toLowerCase().includes(inputValue.toLowerCase())
                                        }
                                        onChange={(value) => {
                                            field.onChange(value)
                                        }
                                        }
                                    />
                                );
                            }}
                        />
                        {<Error>{errors?.bankCode?.message}</Error>}
                    </div>

                    <div>
                        <Controller
                            name="bankAddress"
                            control={control}
                            rules={{required: "Bank address is required"}}
                            render={({field}) => {
                                return (
                                    <Input
                                        label={"Bank address"}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                );
                            }}
                        />
                        {<Error>{errors?.bankAddress?.message}</Error>}
                    </div>
                </>
            ) : (
              <>
                <div>
                    <Controller
                        name="bankCode"
                        control={control}
                        rules={{required: "Mobile money operator is required"}}
                        render={({field}) => {
                            return (
                                <CustomSelect
                                    {...field}
                                    placeholder={"Select mobile money operator"}
                                    $width={"100%"}
                                    options={bankOptions}
                                    showSearch
                                    lightBorder
                                />
                            );
                        }}
                    />
                    {<Error>{errors?.bankCode?.message}</Error>}
                </div>
                <InputWrapper>
                    <Controller
                        name="phoneNumber"
                        control={control}
                        rules={{
                            required: "Beneficiary's phone number is required",
                            validate: validateGhsPhoneNumber,
                        }}
                        render={({field}) => (
                            <InputPhone
                                label="Phone number"
                                placeholder="Enter phone number"
                                field={field}
                                defaultCountry={"GH"}
                                maxLength={GHS_ACCOUNT_NUMBER_MAX_LENGTH}
                                hasError={!!errors?.phoneNumber}
                            />
                        )}
                    />
                    {<Error>{errors?.phoneNumber?.message}</Error>}
                </InputWrapper>
                </>
            )}


            {PAYMENT_METHOD && (
                <div>
                    <Controller
                        name="accountNumber"
                        control={control}
                        rules={{
                            required: "Account number is required",
                            pattern: {
                                value: accountNumberPattern,
                                message: accountNumberErrorMessage,
                            },
                        }}
                        render={({field}) => {
                            return (
                                <Input
                                    label={"Account number"}
                                    inputMode="numeric"
                                    field={{
                                        ...field,
                                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(
                                                sanitizeAccountNumberInput(
                                                    e.target.value,
                                                    GHS_ACCOUNT_NUMBER_MAX_LENGTH,
                                                ),
                                            );
                                        },
                                    }}
                                />
                            );
                        }}
                    />
                    {watchAccountNumber &&
                        watchAccountNumber.length > 0 &&
                        !accountNumberPattern.test(watchAccountNumber) && (
                            <Error>{accountNumberErrorMessage}</Error>
                        )}
                    {<Error>{errors?.accountNumber?.message}</Error>}
                </div>
            )}

            {PAYMENT_METHOD ? (
                <div>
                    <Controller
                        name="accountName"
                        control={control}
                        rules={{validate: accountNameValidator(validateAccountName)}}
                        render={({field}) => {
                            return (
                                <Input
                                    label={"Account name"}
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            );
                        }}
                    />
                    {<Error>{errors?.accountName?.message}</Error>}
                </div>
            ): (

                <div>
                    <Controller
                        name="accountName"
                        control={control}
                        rules={{validate: accountNameValidator(validateAccountName)}}
                        render={({field}) => {
                            return (
                                <Input
                                    label={"Beneficiary name"}
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            );
                        }}
                    />
                    {<Error>{errors?.accountName?.message}</Error>}
                </div>
            )}

            <div>
                <Controller
                    name="paymentDescription"
                    control={control}
                    rules={{required: "Payment description is required"}}
                    render={({field}) => {
                        return (
                            <CustomSelect
                                {...field}
                                options={GHS_PAYMENT_DESCRIPTION}
                                $width="100%"
                                placeholder="Select payment description"
                                showSearch
                                lightBorder
                            />
                        );
                    }}
                />
                {<Error>{errors?.paymentDescription?.message}</Error>}
            </div>

            <ItemWrapper>
                <Button
                    onClick={handlePrevious}
                    label="Prev"
                    height="48px"
                    theme="alternate"
                    width="203px"
                />

                <Button
                    onClick={handleSubmit(onSubmit)}
                    label="Continue"
                    height="48px"
                    disabled={!isValid}
                />
            </ItemWrapper>
        </ContentWrapper>
    );
};

export default Beneficiary;
